import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Wrapper, Container, Tip, Text } from './Tooltip.styled';

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
    id: string;
    tooltip: string;
    mouseDisabled?: boolean;
    borderRadius?: boolean;
    ariaLabel: string;
}

const Tooltip: React.FC<Props> = ({
    children,
    id,
    tooltip,
    mouseDisabled,
    borderRadius,
    ariaLabel
}) => {
    const [isActive, setIsActive] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const isOpen = useMemo(() => isActive || isFocused, [isActive, isFocused]);

    //ref will persist through rerenders
    const isFirstClick = useRef(true);

    useEffect(() => {
        if (isOpen && containerRef.current && tooltipRef.current) {
            const container = containerRef.current;
            const tooltip = tooltipRef.current;

            setTooltipPosition({
                left: container.offsetWidth - 5,
                top: -tooltip.offsetHeight
            });
        }
    }, [isOpen]);

    const handleClick = (event: React.MouseEvent) => {
        event.preventDefault();

        // devices that support hover
        const isHoverable = window.matchMedia('(hover: hover)').matches;
        (event.currentTarget as HTMLElement).blur();

        if (isHoverable) {
            // remove focus immediately after click.
            setIsFocused(false);
            // dismiss on click when on desktops
            setIsActive(false);
        } else {
            // mobile/tablet: enable toggle
            if (isFirstClick.current) {
                setIsActive(true);
                setIsFocused(true);
                isFirstClick.current = false;
            } else {
                setIsActive(!isActive);
                setIsFocused(!isFocused);
            }
        }
    };

    return (
        <Container ref={containerRef}>
            <Wrapper
                aria-describedby={`tooltip_${id}`}
                aria-label={ariaLabel}
                onMouseEnter={!mouseDisabled ? () => setIsActive(true) : undefined}
                onMouseLeave={!mouseDisabled ? () => setIsActive(false) : undefined}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                tabIndex={mouseDisabled ? -1 : 0}
                disabled={mouseDisabled}
                aria-disabled={mouseDisabled}
                $borderRadius={borderRadius}
                onClick={handleClick}
            >
                {children}
            </Wrapper>
            <Tip
                role="tooltip"
                $isOpen={isOpen}
                aria-hidden={!isOpen}
                id={`tooltip_${id}`}
                $position={tooltipPosition}
                ref={tooltipRef}
            >
                <Text>{tooltip}</Text>
            </Tip>
        </Container>
    );
};

export default Tooltip;
